    // src/pages/resources/ChapterOneMarkTab.tsx
    import React, { useState, useEffect } from 'react';
    import {
        Box, Typography, Paper, Button, IconButton, Card, CardContent, Grid,
        Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
        LinearProgress, Tabs, Tab, InputAdornment, Chip
    } from '@mui/material';
    import {
        Add as AddIcon,
        Delete as DeleteIcon,
        Edit as EditIcon,
        CloudUpload as UploadIcon,
        Link as LinkIcon,
        Movie as VideoIcon,
        YouTube as YouTubeIcon,
        Title as TitleIcon,
        Description as DescriptionIcon,
        HelpOutline as QuestionIcon,
        CheckCircle as CheckIcon,
        Image as ImageIcon,
        FormatListNumbered as NumberIcon // Added icon
    } from '@mui/icons-material';
    import { styled } from '@mui/material/styles';
    import {
        setOnemarkVideoByUrl,
        uploadOnemarkVideo,
        deleteOnemarkVideo,
        createOneMarkQuestion,
        updateOneMarkQuestion,
        deleteOneMarkQuestion
    } from '../../services/chapterService';

    // --- Styles ---
    const css = {
        primary: '#174143',
        primaryDark: '#0f2d2f',
        primaryLight: '#1f5961',
        primaryAccent: '#3d9ea4',
    };

    const StyledCard = styled(Card)(({ theme }) => ({
        borderRadius: 16,
        boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        border: `1px solid rgba(23, 65, 67, 0.1)`,
        height: '100%',
        position: 'relative',
        overflow: 'visible'
    }));

    const GradientButton = styled(Button)({
        background: `linear-gradient(to right, ${css.primary}, ${css.primaryLight})`,
        color: 'white',
        padding: '10px 24px',
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            background: `linear-gradient(to right, ${css.primaryDark}, ${css.primary})`,
        },
    });

    function CloudUploadIcon(props: any) {
        return <UploadIcon {...props} />;
    }

    interface Props {
        chapterId: number;
        data: any[]; // Expecting Array of VideoWithQuestionsDto
        refreshData: () => void;
    }

    export default function ChapterOneMarkTab({ chapterId, data, refreshData }: Props) {
        // --- Video Management State ---
        const [mode, setMode] = useState<'URL' | 'FILE'>('URL');
        const [urlForm, setUrlForm] = useState('');
        const [file, setFile] = useState<File | null>(null);
        const [meta, setMeta] = useState({ title: '', description: '', question: '' });
        const [loadingVideo, setLoadingVideo] = useState(false);
        const [progress, setProgress] = useState(0);
        const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

        // --- Question Management State ---
        const [openDialog, setOpenDialog] = useState(false);
        // Added questionNumber to form state
        const [form, setForm] = useState({
            id: null as number | null,
            question: '',
            description: '',
            marks: 1,
            questionNumber: '' as string | number
        });
        const [questionImage, setQuestionImage] = useState<File | null>(null);
        const [snackbar, setSnackbar] = useState<any>(null);

        // Identify current video (Find the first cluster that has a URL)
        const currentVideo = data?.find(cluster => cluster.url);

        useEffect(() => {
            if (currentVideo) {
                setMeta({
                    title: currentVideo.title || '',
                    description: (currentVideo as any).description || '',
                    question: (currentVideo as any).question || ''
                });
                if (currentVideo.url && !currentVideo.url.includes('firebasestorage')) {
                    setUrlForm(currentVideo.url);
                }
            }
        }, [currentVideo]);

        // --- Video Handlers ---

        const handleUrlSubmit = async () => {
            if (!urlForm || !meta.title) return setMsg({ type: 'error', text: 'URL and Title required' });
            setLoadingVideo(true);
            try {
                await setOnemarkVideoByUrl(chapterId, {
                    title: meta.title, url: urlForm, description: meta.description, question: meta.question, displayOrder: 1
                });
                setMsg({ type: 'success', text: 'Video updated' });
                refreshData();
            } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
            finally { setLoadingVideo(false); }
        };

        const handleFileSubmit = async () => {
            if (!file || !meta.title) return setMsg({ type: 'error', text: 'File and Title required' });
            setLoadingVideo(true); setProgress(0);
            try {
                await uploadOnemarkVideo(chapterId, file, meta, (p) => setProgress(p));
                setMsg({ type: 'success', text: 'Uploaded' });
                refreshData(); setFile(null);
            } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
            finally { setLoadingVideo(false); }
        };

        const handleDeleteVideo = async () => {
            if (!window.confirm("Remove One Mark Video?")) return;
            setLoadingVideo(true);
            try {
                await deleteOnemarkVideo(chapterId, true);
                setMsg({ type: 'success', text: 'Removed' });
                setMeta({ title: '', description: '', question: '' }); setUrlForm('');
                refreshData();
            } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
            finally { setLoadingVideo(false); }
        };

        // --- Question Handlers ---

        const handleSaveQuestion = async () => {
            try {
                const payload = {
                    question: form.question,
                    marks: 1,
                    description: form.description,
                    questionNumber: Number(form.questionNumber) || 0 // Use form value
                };

                if (form.id) {
                    await updateOneMarkQuestion(chapterId, form.id, payload, questionImage);
                } else {
                    await createOneMarkQuestion(chapterId, payload, questionImage);
                }

                setOpenDialog(false);
                setQuestionImage(null);
                refreshData();
                setSnackbar({ msg: 'Saved successfully', type: 'success' });
            } catch (e) {
                setSnackbar({ msg: 'Operation Failed', type: 'error' });
            }
        };

        const handleDeleteQuestion = async (id: number) => {
            if (!window.confirm("Delete this question?")) return;
            try {
                await deleteOneMarkQuestion(chapterId, id);
                refreshData();
                setSnackbar({ msg: 'Deleted', type: 'success' });
            } catch (e) {
                setSnackbar({ msg: 'Delete Failed', type: 'error' });
            }
        };

        const handleOpenEdit = (q: any) => {
            setForm({
                id: q.id,
                question: q.question,
                description: q.description || '',
                marks: 1,
                questionNumber: q.questionNumber || '' // Populate from existing data
            });
            setQuestionImage(null);
            setOpenDialog(true);
        };

        const handleOpenCreate = () => {
            // Find max existing number to suggest next
            let nextNum = 1;
            if (data) {
                data.forEach(cluster => {
                    cluster.questions.forEach((q: any) => {
                        if (q.questionNumber && q.questionNumber >= nextNum) nextNum = q.questionNumber + 1;
                    });
                });
            }

            setForm({ id: null, question: '', description: '', marks: 1, questionNumber: nextNum });
            setQuestionImage(null);
            setOpenDialog(true);
        };

        // --- Renders ---

        const renderVideoSection = () => (
            <Grid container spacing={4} sx={{ mb: 6 }}>
                <Grid size={{ xs: 12, md: currentVideo ? 7 : 12 }}>
                    <StyledCard>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, bgcolor: '#fafafa' }}>
                            <Tabs value={mode} onChange={(_, v) => setMode(v)} textColor="primary" indicatorColor="primary">
                                <Tab label="External URL" value="URL" icon={<YouTubeIcon fontSize="small"/>} iconPosition="start" />
                                <Tab label="Upload File" value="FILE" icon={<CloudUploadIcon fontSize="small"/>} iconPosition="start"/>
                            </Tabs>
                        </Box>
                        <CardContent sx={{ p: 4 }}>
                            {msg && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}
                            <Stack spacing={2}>
                                <TextField fullWidth size="small" label="Title" value={meta.title} onChange={e => setMeta({...meta, title: e.target.value})}
                                           InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon color="action"/></InputAdornment> }} />
                                <TextField fullWidth size="small" label="Description" value={meta.description} onChange={e => setMeta({...meta, description: e.target.value})}
                                           InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon color="action"/></InputAdornment> }} />
                                <TextField fullWidth size="small" label="Instructions" value={meta.question} onChange={e => setMeta({...meta, question: e.target.value})}
                                           InputProps={{ startAdornment: <InputAdornment position="start"><QuestionIcon color="action"/></InputAdornment> }} />

                                {mode === 'URL' ? (
                                    <Stack direction="row" spacing={1}>
                                        <TextField fullWidth size="small" label="Video URL" value={urlForm} onChange={e => setUrlForm(e.target.value)}
                                                   InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon color="action"/></InputAdornment> }} />
                                        <GradientButton onClick={handleUrlSubmit} disabled={loadingVideo} startIcon={loadingVideo ? null : <CheckIcon/>}>
                                            {loadingVideo ? 'Saving...' : 'Set'}
                                        </GradientButton>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon/>}>
                                            Select File <input type="file" hidden accept="video/*" onChange={e => setFile(e.target.files?.[0] || null)} />
                                        </Button>
                                        <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>{file?.name}</Typography>
                                        <GradientButton onClick={handleFileSubmit} disabled={!file || loadingVideo} startIcon={loadingVideo ? null : <CloudUploadIcon/>}>
                                            {loadingVideo ? 'Uploading' : 'Upload'}
                                        </GradientButton>
                                    </Stack>
                                )}
                                {loadingVideo && <LinearProgress variant="determinate" value={progress} />}
                            </Stack>
                        </CardContent>
                    </StyledCard>
                </Grid>
                {currentVideo && (
                    <Grid size={{ xs: 12, md: 5 }}>
                        <StyledCard sx={{ overflow: 'hidden' }}>
                            <Box sx={{ background: css.primary, p: 1.5, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VideoIcon fontSize="small"/>
                                <Typography variant="subtitle2" fontWeight={700}>One Mark Video</Typography>
                            </Box>
                            <CardContent>
                                <Box sx={{ bgcolor: '#000', borderRadius: 2, overflow: 'hidden', aspectRatio: '16/9', mb: 1 }}>
                                    <video controls style={{ width: '100%', height: '100%' }}><source src={currentVideo.url} /></video>
                                </Box>
                                <Typography variant="subtitle2">{currentVideo.title}</Typography>
                                <Button size="small" color="error" onClick={handleDeleteVideo}>Remove</Button>
                            </CardContent>
                        </StyledCard>
                    </Grid>
                )}
            </Grid>
        );

        return (
            <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
                {renderVideoSection()}

                <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                    <Typography variant="h6" fontWeight={700} color={css.primary}>1 Mark Questions</Typography>
                    <GradientButton startIcon={<AddIcon />} onClick={handleOpenCreate}>Add Question</GradientButton>
                </Box>

                <Stack spacing={2}>
                    {data?.map((cluster) => (
                        <Box key={cluster.videoId || Math.random()}>
                            {cluster.questions.map((q: any) => (
                                <Card key={q.id} variant="outlined" sx={{ mb: 1, borderColor: '#e0e0e0' }}>
                                    <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                                        <Box display="flex" gap={2} alignItems="flex-start">
                                            <Chip label={`Q${q.questionNumber || '?'}`} color="primary" size="small" />

                                            {q.questionImageUrl && (
                                                <Box
                                                    component="img"
                                                    src={q.questionImageUrl}
                                                    alt="Question"
                                                    sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee' }}
                                                />
                                            )}

                                            <Box flex={1}>
                                                <Typography fontWeight={600} fontSize="0.95rem" gutterBottom>{q.question}</Typography>
                                                {q.description && <Typography variant="body2" color="text.secondary">{q.description}</Typography>}
                                            </Box>
                                            <Box>
                                                <IconButton size="small" color="primary" onClick={() => handleOpenEdit(q)}><EditIcon fontSize="small"/></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteQuestion(q.id)}><DeleteIcon fontSize="small"/></IconButton>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ))}
                    {(!data || data.length === 0) && (
                        <Paper sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderColor: '#ccc' }}>
                            <Typography color="text.secondary">No questions found.</Typography>
                        </Paper>
                    )}
                </Stack>

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{form.id ? 'Edit' : 'Add'} Question</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} mt={1}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Question Number"
                                value={form.questionNumber}
                                onChange={e => setForm({...form, questionNumber: e.target.value})}
                                InputProps={{ startAdornment: <InputAdornment position="start"><NumberIcon color="action"/></InputAdornment> }}
                            />

                            <TextField
                                fullWidth multiline rows={2} label="Question Text"
                                value={form.question} onChange={e => setForm({...form, question: e.target.value})}
                            />

                            <TextField
                                fullWidth multiline rows={2} label="Answer/Description"
                                value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                            />

                            <Box p={2} border="1px dashed #ccc" borderRadius={2} textAlign="center">
                                <Typography variant="caption" display="block" gutterBottom>Question Image (Optional)</Typography>
                                <Button component="label" size="small" startIcon={<ImageIcon/>}>
                                    {questionImage ? 'Change Image' : 'Upload Image'}
                                    <input type="file" hidden accept="image/*" onChange={e => setQuestionImage(e.target.files?.[0] || null)} />
                                </Button>
                                {questionImage && <Typography variant="caption" display="block" mt={1}>{questionImage.name}</Typography>}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveQuestion}>Save</Button>
                    </DialogActions>
                </Dialog>
                <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)}>
                    <Alert severity={snackbar?.type}>{snackbar?.msg}</Alert>
                </Snackbar>
            </Box>
        );
    }